/**
 * Utility functions for sector visualization
 * These functions are used by both single and multi-sector visualization hooks
 */

/**
 * Selects the best remote node from multiple height options
 * Filters nodes with LCC >= 0.25, sorts by LCC (descending) then height (ascending)
 * @param remoteNodes - Array of remote node options for different heights
 * @returns Best height option with index, height, LCC, and remote node data, or null
 */
export const selectBestHeight = (
  remoteNodes: any[]
): {
  index: number;
  height: number;
  LCC: number;
  remote: any;
} | null => {
  if (!remoteNodes || remoteNodes.length === 0) {
    return null;
  }

  const heightOptions: Array<{
    index: number;
    height: number;
    LCC: number;
    remote: any;
  }> = [];

  remoteNodes.forEach((remote, index) => {
    const height = remote?.mount?.height_agl ?? 0;
    const LCC = remote?.aiml_output?.link_close_confidence ?? 0;

    if (LCC >= 0.25) {
      heightOptions.push({ index, height, LCC, remote });
      // Sort by LCC (descending) and height (ascending)
      heightOptions.sort((a, b) => {
        if (b.LCC !== a.LCC) return b.LCC - a.LCC; // Higher LCC first
        return a.height - b.height; // Lower height first if LCC is the same
      });
    }
  });

  return heightOptions.length > 0 ? heightOptions[0] : null;
};

/**
 * Extracts and formats path loss value from remote node data
 * @param remoteInfo - Remote node information object
 * @returns Formatted path loss string (e.g., "120 dB") or empty string
 */
export const getPathLoss = (remoteInfo: any): string => {
  if (remoteInfo?.aiml_output?.path_loss?.average !== undefined) {
    return (
      Math.round(parseFloat(remoteInfo.aiml_output.path_loss.average)) + ' dB'
    );
  }
  return '';
};

/**
 * Extracts numeric path loss value from remote node data
 * @param remoteNode - Remote node data object
 * @returns Numeric path loss value or null if not available
 */
export const getPathLossNumeric = (remoteNode: any): number | null => {
  const pathLoss = remoteNode?.aiml_output?.path_loss?.average;
  if (pathLoss !== undefined) {
    return parseFloat(pathLoss);
  }
  return null;
};

/**
 * Finds the best remote node info based on LCC and pathloss
 * Prioritizes higher LCC first, then lower pathloss when LCC is equal
 * @param infos - Array of remote node info objects with LCC and remote properties
 * @returns Best remote node info or null if array is empty
 */
export const findBestLCCAndPathloss = <T extends { LCC: number; remote: any }>(
  infos: T[]
): T | null => {
  if (infos.length === 0) return null;

  return infos.reduce((best, current) => {
    const bestPathLoss = getPathLossNumeric(best.remote);
    const currentPathLoss = getPathLossNumeric(current.remote);

    if (current.LCC > best.LCC) {
      return current;
    }
    if (current.LCC < best.LCC) {
      return best;
    }

    if (bestPathLoss !== null && currentPathLoss !== null) {
      return currentPathLoss < bestPathLoss ? current : best;
    }
    if (currentPathLoss !== null) {
      return current;
    }
    if (bestPathLoss !== null) {
      return best;
    }

    return best;
  }, infos[0]);
};

/**
 * Calculates the midpoint between two coordinates
 * @param baseNode - Base node coordinates { lat: number, lng: number }
 * @param remoteNode - Remote node coordinates { lat: number, lng: number }
 * @param isSameDirection - Optional flag for handling overlapping lines (not used in single sector)
 * @returns Midpoint coordinates { lat: number, lng: number }
 */
export const getMidpoint = (
  baseNode: { lat: number; lng: number },
  remoteNode: { lat: number; lng: number },
  isSameDirection: boolean = false
): { lat: number; lng: number } => {
  const latMid = (baseNode.lat + remoteNode.lat) / 2;
  const lngMid = (baseNode.lng + remoteNode.lng) / 2;

  return { lat: latMid, lng: lngMid };
};

/**
 * Calculates the distance between two coordinates in meters using Haversine formula
 * @param point1 - First point { lat: number, lng: number }
 * @param point2 - Second point { lat: number, lng: number }
 * @returns Distance in meters
 */
export const calculateDistance = (
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number => {
  const EARTH_RADIUS_METERS = 6371000; // Earth's radius in meters
  const toRadians = (degrees: number) => degrees * (Math.PI / 180);

  const dLat = toRadians(point2.lat - point1.lat);
  const dLng = toRadians(point2.lng - point1.lng);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.lat)) *
      Math.cos(toRadians(point2.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
};

/**
 * Formats a distance in meters to a human-readable string in kilometers
 * @param distanceMeters - Distance in meters
 * @returns Formatted string (e.g., "12.34 km")
 */
export const formatDistance = (distanceMeters: number): string => {
  return `${(distanceMeters / 1000).toFixed(2)} km`;
};

/**
 * Determines the stroke color based on Link Close Confidence (LCC)
 * @param lcc - Link Close Confidence value (0-1)
 * @returns Hex color string or null if LCC is too low
 */
export const getLCCColor = (lcc: number): string | null => {
  if (lcc > 0.75) {
    return '#008000'; // Green
  } else if (lcc > 0.5) {
    return '#FFFF00'; // Yellow
  } else if (lcc > 0.25) {
    return '#FF0000'; // Red
  }
  return null; // LCC <= 0.25 should not be drawn
};
